<template>
  <article class="blog-card bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
    <img 
      :src="post.image || defaultImage" 
      :alt="post.title + ' - Featured Image'" 
      class="w-full h-40 sm:h-48 object-cover"
      loading="lazy"
    >
    <div class="p-3 sm:p-4">
      <h2 class="text-lg sm:text-xl font-semibold mb-2 text-gray-800 dark:text-gray-200 line-clamp-2">{{ post.title }}</h2>
      <p class="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-2 line-clamp-2 sm:line-clamp-3">{{ post.excerpt }}</p>

      <!-- Tags display -->
      <div v-if="post.tags && post.tags.length" class="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
        <span v-for="tag in post.tags" :key="tag"
          class="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
          #{{ tag }}
        </span>
      </div>

      <div class="flex justify-between items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
        <time :datetime="post.createdAt">{{ formatDate(post.createdAt) }}</time>
        <div class="flex items-center space-x-2">
          <button 
            v-if="isAuthenticated"
            @click.stop="toggleLike"
            class="flex items-center space-x-1 transition-colors duration-200"
            :class="localIsLiked ? 'text-red-500 hover:text-red-600' : 'hover:text-blue-500'"
          >
            <!-- Use different SVG icons for liked vs. not liked state -->
            <svg v-if="localIsLiked" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span>{{ localLikes }}</span>
          </button>
          <!-- View Count Display -->
          <span class="flex items-center space-x-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>{{ post.viewCount || 0 }}</span>
          </span>
          <button
            v-if="isAuthenticated"
            @click.stop="toggleBookmark"
            class="transition-colors duration-200"
            :class="localIsBookmarked ? 'text-blue-500 hover:text-blue-600' : 'hover:text-blue-500'"
          >
            <!-- Use different SVG icons for bookmarked vs. not bookmarked state -->
            <svg v-if="localIsBookmarked" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
            </svg>
          </button>
        </div>
      </div>
      <router-link :to="{ name: 'BlogPost', params: { id: post.id } }"
        class="mt-3 sm:mt-4 inline-block font-medium text-sm py-1.5 sm:py-2 px-3 sm:px-4 rounded border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white transition-colors duration-200"
        :aria-label="`Read more about ${post.title}`">
        Read More
      </router-link>
    </div>
  </article>
</template>

<script>
import { useStore } from 'vuex'
import { formatDate } from '@/utils/date'
import { onMounted, watch, ref, computed } from 'vue'

export default {
  name: 'BlogCard',
  props: {
    post: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const store = useStore()
    const defaultImage = ref('https://placehold.co/800x600/png?text=No+Image')
    const isAuthenticated = computed(() => store.getters.isAuthenticated)

    // Local reactive state for immediate UI updates
    const localLikes = ref(props.post.likes || 0)
    const localIsLiked = ref(props.post.isLiked || false)
    const localIsBookmarked = ref(props.post.isBookmarked || false)

    // Watch for prop changes to keep local state in sync
    watch(() => props.post.likes, (newLikes) => {
      localLikes.value = newLikes || 0
    })
    watch(() => props.post.isLiked, (newIsLiked) => {
      localIsLiked.value = newIsLiked || false
    })
    watch(() => props.post.isBookmarked, (newIsBookmarked) => {
      localIsBookmarked.value = newIsBookmarked || false
    })

    const toggleLike = async (event) => {
      event.preventDefault() // Keep this to prevent potential navigation issues if card is wrapped in a link
      if (!isAuthenticated.value) return
      
      try {
        await store.dispatch('toggleLike', props.post.id)
        // Update local reactive state
        if (localIsLiked.value) {
          localLikes.value = (localLikes.value || 1) - 1; // Decrement
        } else {
          localLikes.value = (localLikes.value || 0) + 1; // Increment
        }
        localIsLiked.value = !localIsLiked.value; // Toggle status
      } catch (error) {
        console.error('Error toggling like:', error)
        // Optional: Add user feedback about the error
      }
    }

    const toggleBookmark = async (event) => {
      event.preventDefault()
      if (!isAuthenticated.value) return
      
      try {
        await store.dispatch('toggleBookmark', props.post.id)
        // Update local reactive state
        localIsBookmarked.value = !localIsBookmarked.value; // Toggle status
      } catch (error) {
        console.error('Error toggling bookmark:', error)
        // Optional: Add user feedback about the error
      }
    }

    // Debug: Log post object
    onMounted(() => {
      if (!props.post.id) {
        console.warn('Post object does not have an id property!')
      }
    })

    // Debug: Watch for changes in the post object
    watch(() => props.post, (newPost) => {
      if (!newPost.id) {
        console.warn('Updated post object does not have an id property!')
      }
    }, { deep: true })

    return {
      formatDate,
      toggleLike,
      toggleBookmark,
      defaultImage,
      isAuthenticated,
      localLikes,
      localIsLiked,
      localIsBookmarked
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
